import {
  Box,
  Building2,
  Factory,
  FileSpreadsheet,
  Home,
  Settings,
  Users,
  FolderKanban,
  Package,
  Wallet,
  ListTodo,
  PanelLeftClose,
  PanelLeftOpen,
  TrendingUp,
  Scale,
  Truck,
  Hammer,
  Target,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNavigate, useLocation } from 'react-router-dom';

interface MenuItem {
  icon: any;
  label: string;
  href: string;
}

interface MenuGroup {
  title: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    title: 'Operacional',
    items: [
      { icon: Home, label: 'Início', href: '/dashboard' },
      { icon: FileSpreadsheet, label: 'Ordens de Produção', href: '/dashboard/orders' },
      { icon: Truck, label: 'Romaneios', href: '/dashboard/shipping' },
      { icon: ListTodo, label: 'Tarefas', href: '/dashboard/tasks' },
    ],
  },
  {
    title: 'Estoque & Insumos',
    items: [
      { icon: Box, label: 'Insumos', href: '/dashboard/supplies' },
      { icon: Scale, label: 'Balanço de Estoque', href: '/dashboard/supplies/inventory-balance' },
      { icon: Package, label: 'Produtos Acabados', href: '/dashboard/products' },
    ],
  },
  {
    title: 'Gestão & Custos',
    items: [
      { icon: TrendingUp, label: 'Indicadores', href: '/dashboard/production-indicators' },
      { icon: Target, label: 'Ponto de Equilíbrio', href: '/dashboard/breakeven' },
      { icon: Wallet, label: 'Financeiro', href: '/dashboard/financial' },
    ],
  },
  {
    title: 'Cadastros',
    items: [
      { icon: Users, label: 'Clientes', href: '/dashboard/clients' },
      { icon: Hammer, label: 'Serralheiros', href: '/dashboard/blacksmiths' },
      { icon: Factory, label: 'Metalúrgicas', href: '/dashboard/metallurgies' },
      { icon: Building2, label: 'Fornecedores', href: '/dashboard/suppliers' },
      { icon: FolderKanban, label: 'Projetos', href: '/dashboard/projects' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { icon: Settings, label: 'Configurações', href: '/dashboard/settings' },
    ],
  },
];

interface SidebarProps {
  open?: boolean;
  onToggle?: () => void;
}

export default function Sidebar({ open = true, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (href: string) => {
    if (href === '/dashboard' && location.pathname === '/dashboard') {
      return true;
    }
    return href !== '/dashboard' && location.pathname.startsWith(href);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r bg-card/95 backdrop-blur-md transition-all duration-300 shadow-sm flex flex-col',
        open ? 'w-64' : 'w-16'
      )}
    >
      {/* Header com Logo e Botão de Alternar */}
      <div className="flex h-16 items-center justify-between border-b px-3 shrink-0">
        <div 
          className="flex items-center gap-2.5 cursor-pointer overflow-hidden"
          onClick={() => navigate('/dashboard')}
        >
          <img 
            src="/logo.png" 
            alt="Grupo Plantão" 
            className="h-8 w-8 object-contain rounded-lg bg-white p-0.5 shadow-xs shrink-0" 
          />
          {open && (
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-bold tracking-tight text-foreground truncate">Metalúrgica</span>
              <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Grupo Plantão</span>
            </div>
          )}
        </div>

        {/* Toggle Button */}
        {onToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8 hover:bg-muted text-muted-foreground hover:text-foreground shrink-0"
          >
            {open ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
            <span className="sr-only">
              {open ? 'Recolher menu' : 'Expandir menu'}
            </span>
          </Button>
        )}
      </div>

      {/* Menu com Navegação Categorizada e Rolagem Vertical */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2.5 space-y-4">
        <TooltipProvider delayDuration={0}>
          {menuGroups.map((group, groupIdx) => (
            <div key={group.title} className="space-y-1">
              {open ? (
                <div className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                  {group.title}
                </div>
              ) : (
                groupIdx > 0 && <div className="my-2 border-t border-border/60 mx-1" />
              )}

              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  return (
                    <Tooltip key={item.href}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => navigate(item.href)}
                          className={cn(
                            'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs font-medium transition-all duration-150 relative group',
                            !open && 'justify-center px-0 h-9',
                            active
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold shadow-2xs'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/70'
                          )}
                        >
                          {/* Active Indicator bar */}
                          {active && (
                            <span className="absolute left-0 top-1.5 bottom-1.5 w-1 bg-emerald-600 dark:bg-emerald-400 rounded-r-full" />
                          )}
                          <item.icon
                            className={cn(
                              'h-4 w-4 shrink-0 transition-transform group-hover:scale-105',
                              active
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-muted-foreground group-hover:text-foreground'
                            )}
                          />
                          {open && (
                            <span className="truncate">{item.label}</span>
                          )}
                        </button>
                      </TooltipTrigger>
                      {!open && (
                        <TooltipContent side="right" className="text-xs font-medium">
                          {item.label}
                        </TooltipContent>
                      )}
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          ))}
        </TooltipProvider>
      </nav>

      {/* Rodapé da Sidebar */}
      {open && (
        <div className="p-3 border-t bg-muted/20 shrink-0">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="font-medium">Sistema v2.5</span>
            <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Online
            </span>
          </div>
        </div>
      )}
    </aside>
  );
}